import React, { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Paperclip, Mic, X, FileText } from 'lucide-react';

export const ChatInput = ({
  message,
  setMessage,
  files,
  handleFileChange,
  handlePaste,
  removeFile,
  handleSendMessage,
  handleKeyDown,
  isLoading,
}) => {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-resize textarea height
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [message]);

  // Focus textarea when component mounts and after sending message
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && !isLoading) {
      textarea.focus();
    }
  }, [isLoading]);

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-4">
      <div
        className="bg-background/90 backdrop-blur-xl border border-border/50 relative flex flex-col rounded-3xl shadow-2xl hover:shadow-3xl hover:border-border/70 transition-all duration-300 ring-1 ring-black/5 dark:ring-white/10"
        onPaste={handlePaste}>
        {/* File Preview Area */}
        {files?.length > 0 && (
          <div className="p-4 border-b border-border/50">
            <div className="flex flex-wrap gap-2">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="bg-muted/50 rounded-xl px-3 py-2 flex items-center gap-2 text-sm backdrop-blur-sm border border-border/30">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="truncate max-w-xs font-medium">{file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded-md hover:bg-destructive/10">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center p-2 gap-2">
          <div className="flex items-center gap-1 shrink-0">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              className="hidden"
              accept="image/*,application/pdf"
            />
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl w-10 h-10 hover:bg-primary/10 transition-all duration-200"
              disabled={isLoading}
              onClick={() => fileInputRef.current.click()}
              title="Attach files">
              <Paperclip className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
              <span className="sr-only">Add file</span>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl w-10 h-10 hover:bg-primary/10 transition-all duration-200"
              disabled={isLoading}
              title="Record audio">
              <Mic className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
              <span className="sr-only">Record audio</span>
            </Button>
          </div>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-4 py-4 min-h-[60px] max-h-48 bg-transparent resize-none border-none outline-none placeholder:text-muted-foreground text-foreground hide-scrollbar text-base"
            placeholder="Ask me anything, or add a file..."
            rows={1}
            disabled={isLoading}
          />

          <div className="flex items-center pr-2 gap-1">
            <Button
              onClick={() => handleSendMessage()}
              disabled={isLoading || (!message.trim() && files?.length === 0)}
              className="w-11 h-11 rounded-xl p-0 bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground transition-all duration-200 shadow-sm hover:shadow-md">
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                </svg>
              )}
              <span className="sr-only">Send message</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
